import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { notificationApi } from '../api/jobApi';
import './Header.css';

const LANGUAGES = [
  { code: 'ko', label: 'KR' },
  { code: 'en', label: 'EN' },
  { code: 'vi', label: 'VN' },
  { code: 'km', label: 'KH' },
  { code: 'uz', label: 'UZ' },
];

function Header() {
  const { user, logout, unreadCount, setUnreadCount } = useAuth();
  const { lang, changeLang, t } = useLanguage();
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notiLoading, setNotiLoading] = useState(false);
  const bellRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBellClick = async () => {
    if (bellOpen) { setBellOpen(false); return; }
    setBellOpen(true);
    setNotiLoading(true);
    try {
      const res = await notificationApi.getNotifications();
      setNotifications(res.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setNotiLoading(false);
    }
  };

  const handleNotiClick = async (noti) => {
    if (!noti.read) {
      try {
        await notificationApi.markAsRead(noti.id);
        setNotifications(prev => prev.map(n => n.id === noti.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) { console.error('Failed to mark as read', err); }
    }
    setBellOpen(false);
    if (noti.jobPostId) navigate(`/jobs/${noti.jobPostId}`);
  };

  const formatTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <header className="header">
      <div className="header-inner container">
        <Link to="/" className="logo">OneJob</Link>

        <div className="header-right">
          {user && user.role === 'EMPLOYER' && (
            <Link to="/jobs/create" className="header-link">{t('postJob')}</Link>
          )}

          <div className="lang-pills">
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                className={`lang-pill ${lang === l.code ? 'active' : ''}`}
                onClick={() => changeLang(l.code)}
              >
                {l.label}
              </button>
            ))}
          </div>

          {user ? (
            <>
              <div className="notification-wrapper" ref={bellRef}>
                <button className="icon-btn" onClick={handleBellClick} aria-label={t('notifications') || 'Notifications'}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {unreadCount > 0 && <span className="noti-dot">{unreadCount}</span>}
                </button>
                {bellOpen && (
                  <div className="noti-dropdown">
                    {notiLoading ? (
                      <div className="noti-empty">...</div>
                    ) : notifications.length === 0 ? (
                      <div className="noti-empty">{t('noResults')}</div>
                    ) : (
                      notifications.map(noti => (
                        <div key={noti.id} className={`noti-item ${!noti.read ? 'unread' : ''}`} onClick={() => handleNotiClick(noti)}>
                          <span className="noti-msg">{noti.message}</span>
                          <span className="noti-time">{formatTime(noti.createdAt)}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <Link to="/mypage" className="header-link">{user.name}</Link>
              <button className="btn btn-sm btn-secondary" onClick={logout}>{t('logout')}</button>
            </>
          ) : (
            <button className="btn btn-sm btn-primary" onClick={() => navigate('/login')}>{t('login')}</button>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
